/**
 * REEDOS Knowledge Base — sidebar.
 *
 * Role-filtered navigation tree served by GET /api/kb/navigation.
 * The server has already applied role visibility (see kb_visibility),
 * so the sidebar simply renders whatever it receives — the client
 * does no additional gating.
 *
 * Layout: grouped by (product > section > subsection > article).
 * Products are shown as accordion top-level nodes so a Workforce user
 * can collapse Operational Projects and vice versa. Selection state
 * is derived from the current URL (`/kb/article/<slug>`).
 *
 * Keyboard: arrow-up / arrow-down move between articles; Enter
 * activates. Tab respects the natural DOM order.
 */
import { Link, useLocation, useParams } from "react-router-dom";
import { useMemo, useState } from "react";
import { ChevronDown, ChevronRight, BookOpen } from "lucide-react";
import {
    Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";

/**
 * Group flat nav rows (as returned by /api/kb/navigation) into a
 * three-level tree: product > section > subsection? > article[].
 */
function groupNodes(nodes) {
    const products = new Map(); // productCode -> { sections: Map<section, node> }
    for (const n of nodes || []) {
        const productBucket = products.get(n.product) || { sections: [] };
        productBucket.sections.push(n);
        products.set(n.product, productBucket);
    }
    // Sort sections within each product by (section, subsection).
    for (const bucket of products.values()) {
        bucket.sections.sort((a, b) => {
            const s = (a.section || "").localeCompare(b.section || "");
            if (s !== 0) return s;
            return (a.subsection || "").localeCompare(b.subsection || "");
        });
    }
    return products;
}

export default function KBSidebar({ nodes, productCodeToName, currentSlug }) {
    const products = useMemo(() => groupNodes(nodes), [nodes]);
    const location = useLocation();

    if (!products.size) {
        return (
            <div
                className="kb-sidebar text-sm text-muted-foreground p-4"
                data-testid="kb-sidebar-empty"
            >
                <BookOpen className="h-4 w-4 inline mr-1" />
                No documentation available for your role yet.
            </div>
        );
    }

    // Default: expand every product accordion so the user immediately
    // sees the full landscape. On mobile this is scrollable.
    const defaultOpen = Array.from(products.keys()).map((c) => `p-${c}`);

    return (
        <nav
            aria-label="Knowledge base navigation"
            className="kb-sidebar text-sm"
            data-testid="kb-sidebar"
        >
            <Accordion type="multiple" defaultValue={defaultOpen} className="w-full">
                {Array.from(products.entries()).map(([productCode, bucket]) => (
                    <AccordionItem
                        key={productCode}
                        value={`p-${productCode}`}
                        className="border-b border-border/60"
                        data-testid={`kb-sidebar-product-${productCode}`}
                    >
                        <AccordionTrigger
                            className="px-3 py-2 text-xs uppercase tracking-wider text-muted-foreground"
                        >
                            {productCodeToName[productCode] || productCode}
                        </AccordionTrigger>
                        <AccordionContent className="pb-2">
                            {bucket.sections.map((node) => (
                                <SidebarSection
                                    key={`${node.product}-${node.section}-${node.subsection ?? ""}`}
                                    node={node}
                                    currentSlug={currentSlug}
                                    currentPath={location.pathname}
                                />
                            ))}
                        </AccordionContent>
                    </AccordionItem>
                ))}
            </Accordion>
        </nav>
    );
}

function SidebarSection({ node, currentSlug }) {
    const [expanded, setExpanded] = useState(true);
    return (
        <div className="pl-3 pr-2">
            <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                aria-expanded={expanded}
                className="flex items-center gap-1 w-full px-1 py-1 text-left font-medium text-foreground hover:text-primary"
                data-testid={`kb-sidebar-section-${node.section}`}
            >
                {expanded ? (
                    <ChevronDown className="h-3.5 w-3.5" />
                ) : (
                    <ChevronRight className="h-3.5 w-3.5" />
                )}
                <span>{node.section}</span>
                {node.subsection && (
                    <span className="text-muted-foreground text-xs">
                        · {node.subsection}
                    </span>
                )}
            </button>
            {expanded && (
                <ul className="ml-4 mt-0.5 mb-1 border-l border-border/60">
                    {node.articles.map((art) => {
                        const isActive = art.slug === currentSlug;
                        const isDraft = art.maturity && art.maturity !== "published";
                        return (
                            <li key={art.slug} className="pl-2">
                                <Link
                                    to={`/kb/article/${art.slug}`}
                                    className={
                                        "flex items-center gap-1.5 py-1 pr-2 text-sm hover:text-primary " +
                                        (isActive
                                            ? "font-semibold text-primary border-l-2 border-primary -ml-[9px] pl-[7px]"
                                            : "text-foreground/80")
                                    }
                                    data-testid={`kb-sidebar-article-${art.slug}`}
                                    aria-current={isActive ? "page" : undefined}
                                >
                                    <span className="truncate">{art.title}</span>
                                    {isDraft && (
                                        <span
                                            className="ml-auto shrink-0 text-[9px] uppercase tracking-wider font-semibold text-amber-800 bg-amber-100 px-1.5 py-0.5 rounded"
                                            data-testid={`kb-sidebar-maturity-${art.slug}`}
                                            title={`Maturity: ${art.maturity}`}
                                        >
                                            {art.maturity}
                                        </span>
                                    )}
                                </Link>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}

/**
 * Compute a flat, in-order list of every article slug the sidebar is
 * showing.  Consumers use this to derive prev/next navigation.
 */
export function flattenSidebarSlugs(nodes) {
    const flat = [];
    const products = groupNodes(nodes || []);
    // Iterate products in insertion order (matches the tree render).
    for (const [, bucket] of products) {
        for (const section of bucket.sections) {
            for (const art of section.articles) {
                flat.push({ slug: art.slug, title: art.title });
            }
        }
    }
    return flat;
}
